import { API_ROUTES, NEXTJS_API_ROUTES } from '@/constants/apiPaths';
import { IncomingMessage } from 'http';
import Cookies from 'js-cookie';
import {
    getUserIdFromCookie,
    getCommonHeaders,
    isClient,
} from '@/utils/common';
import {
    AUTH_ENTITY_TYPE,
    AUTH_KEYS,
    GARAGE_FRONTEND_SOURCE_ID,
} from '@/constants/common';
import {
    ApplyData,
    IServiceReminder,
    RewardConditions,
    FetchRewardResponseDto,
    CDPData,
    PincodeByGarageIdResponse,
} from '@/types';
import { getAppointmentDetails } from '@/components/fakeDoor/api';
import { IAppointmentData, IVehicleInfo } from '@/components/fakeDoor/types';
import { USER_ADDRESS_SOURCE } from '@/constants/enums';

export const moveTimelineStage = async (payload: any) => {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    myHeaders.append(AUTH_KEYS.X_SOURCE_ID, GARAGE_FRONTEND_SOURCE_ID);
    myHeaders.append(AUTH_KEYS.X_ENTITY, AUTH_ENTITY_TYPE.ORDER);
    myHeaders.append(AUTH_KEYS.X_ENTITY_ID, payload?.request_id);

    const requestOptions: RequestInit = {
        method: 'POST',
        headers: myHeaders,
        credentials: 'include',
        body: JSON.stringify(payload),
    };

    try {
        const response = await fetch(
            NEXTJS_API_ROUTES.GARAGE_TIMELINE,
            requestOptions
        );
        await changePageStatus(payload?.request_id);
        if (!response.ok) {
            console.error('Something went wrong');
        }
        return await response.json();
    } catch (error) {
        console.error(`Error moving timeline data`);
    }
};

export const changePageStatus = async (id: any) => {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    myHeaders.append(AUTH_KEYS.X_SOURCE_ID, GARAGE_FRONTEND_SOURCE_ID);
    myHeaders.append(AUTH_KEYS.X_ENTITY, AUTH_ENTITY_TYPE.ORDER);
    myHeaders.append(AUTH_KEYS.X_ENTITY_ID, id);

    const requestOptions: RequestInit = {
        method: 'POST',
        headers: myHeaders,
        credentials: 'include',
        body: JSON.stringify({
            is_welcome_screen_viewed: true,
        }),
    };

    try {
        const response = await fetch(
            `${NEXTJS_API_ROUTES.GARAGE_TIMELINE}/${id}`,
            requestOptions
        );
        if (!response.ok) {
            console.error('Something went wrong');
        }
        return await response.json();
    } catch (error) {
        console.error(`Error moving screen status`);
    }
};
//Call NextJS server api if we want to call this api from client
// export const fetchUserDetails = async () => {
//     const myHeaders = new Headers();
//     myHeaders.append('Content-Type', 'application/json');

//     try {
//         const response = await fetch(API_ROUTES.GET_USER_DETAILS, {
//             credentials: 'include',
//         });
//         console.log('server-user-details', response);
//         if (!response.ok) {
//             console.error('Something went wrong');
//         }
//         return await response.json();
//     } catch (error) {
//         console.error(``);
//     }
// };

const getBaseUrl = (): string => {
    if (typeof window !== 'undefined') {
        // If running in the browser, return the current origin
        return window.location.origin;
    }

    return process.env.NEXT_PUBLIC_ENV === 'PRODUCTION'
        ? 'https://www.acko.com'
        : 'https://www.ackodev.com';
};

export const fetchUserDetailsServer = async (req: IncomingMessage) => {
    console.log('req headers in fetchUserDetailsServer', req.headers);
    const headers: Record<string, string> = getCommonHeaders(req.headers);
    const url = API_ROUTES.GET_USER_DETAILS(headers[AUTH_KEYS.X_AUTH_USER_ID]);

    try {
        const response = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                cookie: req.headers.cookie || '',
            },
        });
        if (!response.ok) {
            console.error('Something went wrong');
        }
        return await response.json();
    } catch (error) {
        console.error(``);
    }
};

export const consoleLog = (logData: string) => {
    const isProd = process.env.NEXT_PUBLIC_ENV === 'PRODUCTION';

    if (isProd && typeof window !== 'undefined') {
        const userId = getUserIdFromCookie();
        const url = window.location.href;

        const payload = `${logData} | URL: ${url} | UserID: ${userId}`;
        fetch(
            `${window.location.origin}${process.env.NEXT_PUBLIC_ENV_PREFIX}/api/logger`,
            {
                method: 'POST',
                body: JSON.stringify(payload),
                headers: { 'Content-Type': 'application/json' },
            }
        );
    }
};

export const fetchOfferValidation = async (
    garageId: string,
    carNumber: string,
    color?: string
) => {
    try {
        //To do:- Update base path
        let apiUrl = NEXTJS_API_ROUTES.QR_CAMPAIGN_OFFER_VALIDATION(
            carNumber,
            garageId
        );
        if (color) {
            apiUrl = apiUrl + `&color=${color}`;
        }

        const response = await fetch(apiUrl, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'X-WorkShop-Id': garageId,
                'Content-Type': 'application/json',
            },
        });
        console.log(response);

        if (!response.ok) {
            throw new Error('Failed to fetch offer details');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching offer details:', error);
        return null;
    }
};

//todo - garage_id is required as query param
export const fetchCampaignDetails = async (
    utmMedium: string,
    workshopId: string
) => {
    try {
        //To do update the base path
        const apiUrl = NEXTJS_API_ROUTES.QR_CAMPAIGN_DETAILS(
            encodeURIComponent(utmMedium)
        );

        const myHeaders = new Headers();
        myHeaders.append('X-WorkShop-Id', workshopId);

        const requestOptions: RequestInit = {
            method: 'GET',
            headers: myHeaders,
        };

        // API request
        const response = await fetch(apiUrl, requestOptions);

        if (!response.ok) {
            throw new Error(
                `Failed to fetch campaign details: ${response.statusText}`
            );
        }

        // Return response as JSON
        return await response.json();
    } catch (error) {
        console.error(
            `Error fetching campaign details: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
        return null; // Handle errors gracefully
    }
};

//todo - garage_id is required as query param
export const fetchColors = async (garageId: string) => {
    try {
        const apiUrl =
            NEXTJS_API_ROUTES.QR_CAMPAIGN_COLORS('qr_online_campaign');

        const response = await fetch(apiUrl, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'X-WorkShop-Id': garageId,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error('Failed to fetch color details');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching color details:', error);
        return null;
    }
};

export async function fetchAppointmentDetailsFromOrderId(
    req: any,
    appointmentIdFromQuery?: string | string[]
): Promise<any> {
    const appointmentId = Array.isArray(appointmentIdFromQuery)
        ? appointmentIdFromQuery[0]
        : appointmentIdFromQuery;

    if (appointmentId) {
        try {
            return await getAppointmentDetails(appointmentId, req);
        } catch (error) {
            console.error('Error fetching appointment details:', error);
            return {};
        }
    }

    const headers = getCommonHeaders(req.headers);
    let appointmentData = {};

    try {
        const response = await fetch(API_ROUTES.FETCH_ORDERID, {
            method: 'GET',
            headers,
        });

        if (!response.ok) {
            console.log(
                `API call failed with status: ${response.status}`,
                response
            );
            throw new Error('Failed to fetch Order ID');
        }

        const res = await response.json();
        console.log('API response parsed: ', res);

        if (res?.appointment_id) {
            try {
                appointmentData = await getAppointmentDetails(
                    res?.appointment_id as string,
                    req
                );
            } catch (error) {
                console.error('Error fetching appointment details:', error);
                // Return empty object on error
            }
        }
    } catch (error) {
        console.error('Error occurred while fetching Order ID:', error);
    }

    return appointmentData;
}

export const upsertUserAddress = async (payload: IAppointmentData) => {
    const parsedPayload = {
        address: { ...payload.pickup_details.address },
        registration_number: payload.vehicle_details.registration_number,
        source: USER_ADDRESS_SOURCE.MOBILE_APP,
    };
    const requestOptions: RequestInit = {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(parsedPayload),
    };

    try {
        const response = await fetch(
            NEXTJS_API_ROUTES.USERS_ADDRESS,
            requestOptions
        );
        if (!response.ok) {
            console.error('Something went wrong trying to upset user address');
        }
        return await response.json();
    } catch (error) {
        console.error(`Error trying to upsert user address`);
    }
};

export const createRepeatBooking = async (
    garage_id: string | '',
    vehicle_registration_number: string | '',
    service_type: string | '',
    car_details: IVehicleInfo,
    req: IncomingMessage
) => {
    const {
        registration_number,
        make_name,
        model_name,
        acko_model_id,
        fuel_type,
        color,
        variant_name,
    } = car_details;
    const payload = {
        garage_id,
        vehicle_registration_number,
        service_type,
        booking_journey: 'OLD_BOOKING_FLOW',
        vehicle_details: {
            registration_number: registration_number,
            make: make_name,
            model: model_name,
            model_id: acko_model_id,
            fuel_type,
            color: color,
            vin: variant_name,
        },
    };
    const headers: Record<string, string> = getCommonHeaders(req.headers);
    try {
        const response = await fetch(API_ROUTES.REPEAT_APPOINTMENT, {
            method: 'POST',
            headers,
            body: JSON.stringify(payload),
        });
        if (!response.ok) {
            console.error(
                'Something went wrong trying to create repeat booking',
                response
            );
            return '';
        }
        return await response.json();
    } catch (error) {
        console.error(`Error trying to create repeat booking`, error);
        return '';
    }
};

export async function fetchAppointmentServerAPI(
    req: IncomingMessage,
    appointmentId: string
): Promise<any> {
    const url = `${API_ROUTES.APPOINTMENT}${appointmentId}`;
    const headers = {
        ...getCommonHeaders(req.headers),
        [AUTH_KEYS.X_ENTITY]: AUTH_ENTITY_TYPE.APPOINTMENT,
        [AUTH_KEYS.X_ENTITY_ID]: appointmentId,
    };
    try {
        const response: any = await fetch(url, {
            method: 'GET',
            headers,
        });
        return await response.json();
    } catch (error) {
        console.error(`failed fetching appointment details`);
    }
}

export async function fetchServiceReminders(
    req: IncomingMessage,
    vehicle_registration_number: string
) {
    const url = `${API_ROUTES.SERVICE_REMINDERS}/?registration_number=${vehicle_registration_number}`;
    const headers = getCommonHeaders(req.headers);
    try {
        const response = await fetch(url, {
            headers,
        });
        return await response.json();
    } catch (error) {
        console.error(`failed fetching service reminders`, error);
    }
}

export async function updateServiceReminder(
    params: IServiceReminder,
    selectedDate: string
) {
    const { carNo, serviceType, lastServiceDate, garageId, appointmentId } =
        params;
    const payload = {
        car_no: carNo,
        service_type: serviceType,
        next_service_date: selectedDate,
        last_service_date: lastServiceDate,
        garage_id: garageId,
        appointment_id: appointmentId,
        operation_by: 'USER',
    };

    const url = NEXTJS_API_ROUTES.SERVICE_REMINDERS;
    try {
        const response = await fetch(url, {
            method: 'PUT',
            body: JSON.stringify(payload),
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok) {
            console.error(
                'Something went wrong updating service reminder',
                response
            );
            return '';
        }
        return await response.json();
    } catch (error) {
        console.error(`failed updating service reminder`, error);
    }
}

export async function getAllGarageDetails(): Promise<any> {
    const url = `${API_ROUTES.GET_GARAGE_DETAILS}`;
    console.log({ url });
    try {
        const res = await fetch(url);
        console.log({ res });
        if (res.ok) {
            const parsedRes = await res.json();
            return parsedRes;
        } else {
            throw new Error('Something went wrong trying to fetch garage data');
        }
    } catch (err) {
        console.error('Error trying to fetch garage data');
    }
}

export async function fetchRewardDetails(
    req: any,
    data: RewardConditions
): Promise<FetchRewardResponseDto | undefined> {
    const headers = getCommonHeaders(req.headers);
    const url = `${API_ROUTES.FETCH_REWARD_DETAILS}`;
    try {
        const res = await fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify(data),
        });
        if (res.ok) {
            const parsedRes = await res.json();
            return parsedRes;
        } else {
            throw new Error('Error fetching reward details');
        }
    } catch (err) {
        console.error(`Error fetching reward details`, err);
    }
}

export async function extractRewardDetails(data: ApplyData): Promise<any> {
    const url = `${NEXTJS_API_ROUTES.EXTRACT_REWARD_DETAILS}`;
    try {
        const res = await fetch(url, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });
        if (res.ok) {
            const parsedRes = await res.json();
            return parsedRes;
        } else {
            throw new Error('Error extracting reward details');
        }
    } catch (err) {
        console.error(`Error extracting reward details`, err);
    }
}

export async function getCouponDetailsFromId(
    req: any,
    rewardId: string
): Promise<any> {
    const headers = getCommonHeaders(req.headers);
    const url = `${API_ROUTES.GET_REWARD_DETAILS_FROM_ID}/${rewardId}`;
    try {
        const res = await fetch(url, {
            headers,
        });
        if (res.ok) {
            const parsedRes = await res.json();
            return parsedRes;
        } else {
            throw new Error('Error getting coupon details from id');
        }
    } catch (err) {
        console.error(`Error getting coupon details from id`, err);
    }
}

export async function getBookingAmount(
    couponCode: string,
    garageId: string,
    serviceType: string
): Promise<any> {
    const url = `${NEXTJS_API_ROUTES.GET_BOOKING_AMOUNT(couponCode, garageId, serviceType)}`;
    console.log({ url });
    try {
        const res = await fetch(url);
        console.log({ res });
        if (res.ok) {
            const parsedRes = await res.json();
            return parsedRes;
        } else {
            throw new Error(
                'Something went wrong trying to fetch booking amount'
            );
        }
    } catch (err) {
        console.error('Error trying to fetch booking amount');
    }
}

export async function getUserPolicy(req: IncomingMessage) {
    const url = API_ROUTES.USER_POLICY_PROFILE;
    const headers = getCommonHeaders(req.headers);
    try {
        const response: any = await fetch(url, {
            method: 'GET',
            headers,
        });
        return await response.text();
    } catch (error) {
        console.error(`failed fetching user policy, error: `, error);
    }
}

export async function getCdpData(
    req: IncomingMessage
): Promise<CDPData | undefined> {
    const url = API_ROUTES.GET_CDP_DATA;
    const headers = getCommonHeaders(req.headers);
    try {
        const response: any = await fetch(url, {
            method: 'GET',
            headers,
        });
        if (response.ok) {
            const parsedRes = await response.json();
            return parsedRes;
        } else {
            throw new Error('Error fetching cdp data');
        }
    } catch (err) {
        console.error(`Error fetching cdp data`, err);
    }
}

export async function getPincodeByGarageId(
    garageId: string,
    req: IncomingMessage
): Promise<PincodeByGarageIdResponse | undefined> {
    let url = '';
    if (isClient()) {
        url = NEXTJS_API_ROUTES.GET_PINCODE_BY_GARAGE_ID;
    } else {
        url = API_ROUTES.GET_PINCODE_BY_GARAGE_ID;
    }
    try {
        const response: any = await fetch(url, {
            method: 'GET',
            headers: {
                ...getCommonHeaders(req.headers),
                'x-workshop-id': garageId,
                credentials: 'include',
            },
        });
        if (response.ok) {
            const parsedRes = await response.json();
            return parsedRes;
        } else {
            throw new Error('Error fetching pincode by garage id');
        }
    } catch (err) {
        console.error(`Error fetching pincode by garage id`, err);
    }
}

export const fetchFEDetails = async (
    jobId: number,
    req: IncomingMessage
): Promise<any> => {
    const url = `${API_ROUTES.PUD_TRACKER}/${jobId}`;
    console.log('faehjdg', url);
    const headers = getCommonHeaders(req.headers);
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers,
        });
        if (!response.ok) {
            console.log(
                `FE Details API failed with status: ${response.status}`
            );
            return null;
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching FE details:', error);
        return null;
    }
};

export const fetchGarageTimelineServer = async (
    orderId: string,
    req: IncomingMessage
): Promise<any> => {
    const headers = {
        ...getCommonHeaders(req.headers),
        [AUTH_KEYS.X_SOURCE_ID]: GARAGE_FRONTEND_SOURCE_ID,
        [AUTH_KEYS.X_ENTITY]: [AUTH_ENTITY_TYPE.ORDER],
        [AUTH_KEYS.X_ENTITY_ID]: orderId,
    } as Record<string, string>;

    try {
        const response = await fetch(
            `${API_ROUTES.GARAGE_TIMELINE}/${orderId}`,
            {
                method: 'GET',
                headers,
            }
        );

        if (!response.ok) {
            console.error(
                `Failed to fetch garage timeline: ${response.status}`
            );
            return null;
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching garage timeline:', error);
        return null;
    }
};
